import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { BulkConversationAction } from "@redotech/effect-api-shared/models/shopper-ai/conversation/bulk-conversation-action";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";

export function useBulkConversationAction(): {
  pendingAction: BulkConversationAction | null;
  startBulkConversationAction: (input: {
    action: BulkConversationAction;
    conversationIds: readonly ConversationId[];
  }) => Promise<void>;
} {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const [pendingAction, setPendingAction] =
    useState<BulkConversationAction | null>(null);

  const startBulkConversationAction = useHandler(
    async ({
      action,
      conversationIds,
    }: {
      action: BulkConversationAction;
      conversationIds: readonly ConversationId[];
    }) => {
      if (conversationIds.length === 0) {
        return;
      }

      setPendingAction(action);
      try {
        await effectApi.runPromise(
          catchApiErrors(
            effectApi
              .getClient()
              .conversation.startBulkConversationAction({
                payload: { action, conversationIds: [...conversationIds] },
              }),
          ),
        );
      } catch {
        toast("Failed to start bulk action. Please try again.", {
          variant: "error",
        });
      } finally {
        setPendingAction(null);
      }
    },
  );

  return { pendingAction, startBulkConversationAction };
}
